import { seriesCanvasHeatmap } from "d3fc"
import { getGapIndexes, getTimeSeriesData, TimeSeriesData } from "../../../../Data/TimeSeriesData"
import { HeatmapTraceConfig, PageRectangle, getHeatmapTraceId } from "../../../../Types/Trace"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { D3Trace } from "./D3Trace"
import { TraceRenderStrategy, TraceRendererOptions } from "./RenderStrategy"
import { ScaleSequential, ScaleTime, scaleSequential, scaleTime } from "d3"
import { getSpectrumInterpolator } from "../../../../Types/ColorSpectrum"
import { ReactCallbacks } from "../../../../Types/ReactCallbacks"
import { getColors, MobergTheme } from "../../../../../../../../Moberg"

export class HeatmapRenderStrategy extends TraceRenderStrategy {
	private directRenderer = seriesCanvasHeatmap()
	private offscreenRenderer = seriesCanvasHeatmap()

	private pageManager: TimeSeriesPageManager<ModalityPage>
	private config: HeatmapTraceConfig
	private d3Trace: D3Trace
	private colorScale: ScaleSequential<string>
	private renderingXScale = scaleTime()
	private get placeholderColor() {
		return getComputedStyle(document.documentElement).getPropertyValue("--color-chart-placeholder").trim() || getColors(MobergTheme.GRAY).main
	}

	constructor(pageManager: TimeSeriesPageManager<ModalityPage>, d3Trace: D3Trace, config: HeatmapTraceConfig) {
		super()
		this.config = config
		this.d3Trace = d3Trace
		this.pageManager = pageManager

		this.directRenderer
			.xValue((d: [string, string, number]) => d[0])
			.yValue((d: [string, string, number]) => d[1])
			.colorValue((d: [string, string, number]) => d[2])
			.yScale(this.config.yScale)

		this.offscreenRenderer
			.xValue((d: [string, string, number]) => d[0])
			.yValue((d: [string, string, number]) => d[1])
			.colorValue((d: [string, string, number]) => d[2])
			.yScale(this.config.yScale)

		this.colorScale = scaleSequential(getSpectrumInterpolator(this.config.colorSpectrum))
		this.colorScale.domain([this.config.lowerBound, this.config.upperBound])
	}

	public renderPage(
		page: ModalityPage,
		reactCallbacks: ReactCallbacks<any>,
		offscreenXScale: ScaleTime<any, any, any>,
		offscreenCanvas: OffscreenCanvas,
		context: CanvasRenderingContext2D,
		offscreenContext: OffscreenCanvasRenderingContext2D,
		pageRectangle: PageRectangle,
		unloadedRectangle: PageRectangle | null,
	) {
		const { x, y, width } = pageRectangle
		const dataObjectId = reactCallbacks.dataSourceMap.get(this.config.dataSource) ?? Infinity
		const traceData = page.data.get(dataObjectId)?.get(this.config.dataKey)

		let edges: [number | undefined, number][] = []

		this.offscreenFillInUnloadedRegion(offscreenContext, unloadedRectangle)

		if (traceData !== undefined && traceData.data.length > 0) {
			offscreenXScale.domain([page.startTime, page.endTime]).range([0, width])
			const timeSeriesData = getTimeSeriesData(traceData, this.config)
			edges = this.renderTimeSeriesData(timeSeriesData, page, this.getOffscreenRenderer({ xScale: offscreenXScale, yScale: this.config.yScale }))
		}

		const bitmap = offscreenCanvas.transferToImageBitmap()
		page.updateRenderCache(dataObjectId, this.getRenderCacheKey(), { bitmap, dirty: false, edges })
		context.drawImage(bitmap, x, y)
	}

	getRenderCacheKey(): string {
		let compositeIndex = undefined

		if (this.config.isCompositePart) {
			compositeIndex = this.config.compositeIndex
		}

		const { graphId, dataKey, colorSpectrum, lowerBound, upperBound } = this.config as HeatmapTraceConfig
		const dataObjectId = this.d3Trace.getDataObjectId(this.config) ?? Infinity
		const colorScheme = document.documentElement.getAttribute("data-color-scheme") ?? "light"
		return [graphId, dataKey, compositeIndex, dataObjectId, colorSpectrum, lowerBound, upperBound, colorScheme].join("-")
	}

	public updateConfig(traceConfig: HeatmapTraceConfig) {
		this.config = traceConfig
		this.colorScale = scaleSequential(getSpectrumInterpolator(this.config.colorSpectrum)).domain([this.config.lowerBound, this.config.upperBound])
	}

	public getOffscreenRenderer = (options?: TraceRendererOptions) => {
		if (options?.yScale) {
			this.offscreenRenderer.yScale(this.config.yScale.range(options.yScale.range()))
		}

		return this.offscreenRenderer
	}

	public getDirectRenderer = (options?: TraceRendererOptions) => {
		if (options?.yScale) {
			this.directRenderer.yScale(this.config.yScale.range(options.yScale.range()))
		}

		return this.directRenderer
	}

	public render() {
		this.pageManager.getPagesInView().forEach(page => this.d3Trace.renderPage(page))
	}

	public renderTimeSeriesData(data: TimeSeriesData, page: ModalityPage, renderer: any) {
		// If there is only one data point, just pretend that the value is constant.
		if (data.data.length === 1) {
			data = {
				data: new Float32Array([data.data[0], data.data[0]]),
				times: [page.startTime, page.endTime],
			}
		}

		const context = renderer.context()
		const yBandWidth = this.config.yScale.bandwidth()
		const heatmapTraceId = getHeatmapTraceId(this.config)
		const yOffset = this.config.yScale(heatmapTraceId) ?? 0
		const xScale = this.renderingXScale.domain([page.startTime, page.endTime]).range([0, page.width])
		const timingInformation = page.timingInformation.get(this.d3Trace.dataObjectId)?.get(this.config.dataKey)
		const samplingPeriod = timingInformation?.samplingPeriod ?? 0
		const gapIndexes = getGapIndexes(timingInformation?.gapIndexes ?? [], this.config)
		const gapIndexesSet = new Set(gapIndexes)

		for (let i = 0; i < data.times.length; i++) {
			const currentValue = data.data[i]
			const currentTime = data.times[i]

			// Get the start and end time of the rectangle. Fill to the start/end of the page if the gap is reasonably small enough.
			const startTime = i === 0 && data.times[i] - page.startTime <= samplingPeriod ? page.startTime : currentTime

			let endTime

			if (i < data.times.length - 1) {
				endTime = data.times[i + 1]
			} else if (i === data.times.length - 1 && page.endTime - currentTime <= samplingPeriod) {
				endTime = page.endTime
			} else {
				endTime = startTime
			}

			const startX = xScale(startTime)
			const width = xScale(endTime) - startX

			if (width <= 0) {
				continue
			}

			context.fillStyle = i !== data.times.length - 1 && gapIndexesSet.has(i + 1) ? this.placeholderColor : this.colorScale(currentValue)

			// Rounding prevents drawing on half pixels
			context.fillRect(Math.floor(startX), yOffset, Math.ceil(width), yBandWidth)
		}

		return []
	}
}
